/**
 * Member Analytics Section Component for v0.6.0
 * Full analytics section with filters, charts, and export functionality
 */

import { useState, useEffect } from 'react';
import AnalyticsChart from './AnalyticsChart';
import AnalyticsTable from './AnalyticsTable';
import { getMemberAnalytics } from '../../../services/analyticsApi';
import type { MemberAnalytics, AnalyticsQuery } from '../../../types';
import './MemberAnalyticsSection.css';
import './AnalyticsChart.css';

interface MemberAnalyticsSectionProps {
  className?: string;
}

const MemberAnalyticsSection = ({ className = '' }: MemberAnalyticsSectionProps) => {
  const [analytics, setAnalytics] = useState<MemberAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Default to last 30 days
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [startDate, endDate]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const query: AnalyticsQuery = {};
      if (startDate) query.startDate = startDate;
      if (endDate) query.endDate = endDate;

      const response = await getMemberAnalytics(query);
      setAnalytics(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`member-analytics-section member-analytics-section--loading ${className}`}>
        <div className="spinner"></div>
        <p>載入中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`member-analytics-section member-analytics-section--error ${className}`}>
        <p className="error-message">{error}</p>
        <button onClick={loadAnalytics} className="btn btn--primary">重試</button>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <div className={`member-analytics-section ${className}`}>
      {/* Header with controls */}
      <div className="member-analytics-section__header">
        <div className="member-analytics-section__controls">
          {/* View toggle */}
          <div className="member-analytics-section__toggle">
            <button
              className={`btn btn--outline ${viewMode === 'chart' ? 'btn--active' : ''}`}
              onClick={() => setViewMode('chart')}
            >
              圖表
            </button>
            <button
              className={`btn btn--outline ${viewMode === 'table' ? 'btn--active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              表格
            </button>
          </div>

          {/* Date filters */}
          <div className="member-analytics-section__filters">
            <label className="member-analytics-section__filter">
              <span className="member-analytics-section__filter-label">開始日期</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="member-analytics-section__input"
              />
            </label>
            <label className="member-analytics-section__filter">
              <span className="member-analytics-section__filter-label">結束日期</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="member-analytics-section__input"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'chart' ? (
        <AnalyticsChart analytics={analytics} />
      ) : (
        <AnalyticsTable analytics={analytics} />
      )}
    </div>
  );
};

export default MemberAnalyticsSection;
